import { useEffect, useMemo, useState, useCallback } from "react";

const TOKEN_KEY = "sz_admin_token";

// ─── API client ───────────────────────────────────────────────────────────────
const apiCall = async (path, params = {}, opts = {}) => {
  const token = sessionStorage.getItem(TOKEN_KEY);
  const url = new URL(`/api/admin/${path}`, window.location.origin);
  if (opts.method !== "POST") {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, v);
    });
  }
  url.searchParams.set("_t", Date.now()); // cache buster
  const init = {
    method: opts.method || "GET",
    headers: { "x-admin-token": token || "" },
    cache: "no-store",
  };
  if (opts.method === "POST") {
    init.headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(params);
  }
  const res = await fetch(url, init);
  if (res.status === 401) {
    sessionStorage.removeItem(TOKEN_KEY);
    window.location.reload();
    throw new Error("Unauthorized");
  }
  if (!res.ok) throw new Error((await res.json()).error || "Request failed");
  return res.json();
};

// ─── CSS ─────────────────────────────────────────────────────────────────────
const CSS = `
  *{box-sizing:border-box}
  body{margin:0;background:#0e1410;color:#cfe6da;font-family:'Inter','Segoe UI',sans-serif}
  .a-shell{display:flex;min-height:100vh}
  .a-side{width:220px;background:#0a100c;border-right:1px solid #1c2b25;padding:24px 0;flex-shrink:0}
  .a-logo{padding:0 24px 24px;border-bottom:1px solid #1c2b25;margin-bottom:16px;display:flex;align-items:center;gap:10px}
  .a-logo-svg{flex-shrink:0}
  .a-logo-text{display:flex;flex-direction:column;gap:2px}
  .a-logo-name{font-family:'DM Mono',monospace;font-size:13px;font-weight:700;color:#7ecfb0;letter-spacing:0.2em}
  .a-logo-sub{font-size:10px;color:#5a8a78;letter-spacing:0.18em;text-transform:uppercase;font-family:'DM Mono',monospace;font-weight:400}
  .a-nav{display:flex;flex-direction:column;gap:2px;padding:0 12px}
  .a-nav button{background:none;border:none;color:#7aad98;text-align:left;padding:10px 14px;border-radius:8px;cursor:pointer;font-size:14px;font-family:inherit;transition:all .15s}
  .a-nav button:hover{background:#13201a;color:#cfe6da}
  .a-nav button.active{background:#1c2b25;color:#7ecfb0;font-weight:600}
  .a-main{flex:1;padding:32px 40px;overflow-x:auto}
  .a-h{display:flex;justify-content:space-between;align-items:center;margin-bottom:24px}
  .a-h h1{font-family:'Playfair Display',serif;font-size:28px;font-weight:700;color:#cfe6da;margin:0}
  .a-btn{background:#1c5448;color:#cfe6da;border:none;border-radius:8px;padding:8px 14px;font-size:13px;cursor:pointer;font-family:inherit;transition:background .15s}
  .a-btn:hover{background:#2d7a62}
  .a-btn-ghost{background:transparent;border:1px solid #1c2b25;color:#7aad98}
  .a-btn-ghost:hover{background:#13201a;color:#cfe6da}
  .a-cards{display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:14px;margin-bottom:32px}
  .a-card{background:#13201a;border:1px solid #1c2b25;border-radius:12px;padding:18px}
  .a-card-l{font-size:11px;color:#5a8a78;letter-spacing:0.1em;text-transform:uppercase;margin-bottom:8px}
  .a-card-v{font-family:'Playfair Display',serif;font-size:32px;font-weight:700;color:#7ecfb0}
  .a-card-s{font-size:11px;color:#7aad98;margin-top:4px}
  .a-filters{display:flex;gap:10px;align-items:center;flex-wrap:wrap;margin-bottom:18px;padding:14px;background:#13201a;border:1px solid #1c2b25;border-radius:10px}
  .a-filters input,.a-filters select{background:#0a100c;border:1px solid #1c2b25;color:#cfe6da;border-radius:6px;padding:7px 10px;font-size:13px;font-family:inherit}
  .a-filters input::placeholder{color:#5a8a78}
  .a-filters input:focus,.a-filters select:focus{outline:none;border-color:#2d7a62}
  .a-filter-l{font-size:11px;color:#5a8a78;letter-spacing:0.06em;text-transform:uppercase;margin-right:4px}
  .a-table{width:100%;border-collapse:collapse;background:#13201a;border:1px solid #1c2b25;border-radius:10px;overflow:hidden}
  .a-table th{text-align:left;padding:12px 14px;font-size:11px;color:#5a8a78;letter-spacing:0.08em;text-transform:uppercase;background:#0a100c;border-bottom:1px solid #1c2b25;cursor:pointer;user-select:none;white-space:nowrap}
  .a-table th:hover{color:#cfe6da}
  .a-table td{padding:11px 14px;font-size:13px;border-bottom:1px solid #1c2b25;vertical-align:top;color:#cfe6da}
  .a-table tr:last-child td{border-bottom:none}
  .a-table tr.clickable{cursor:pointer;transition:background .15s}
  .a-table tr.clickable:hover td{background:#1a2922}
  .a-mono{font-family:'DM Mono','Fira Code',monospace;font-size:12px}
  .a-pill{display:inline-block;padding:2px 8px;border-radius:10px;font-size:11px;font-family:'DM Mono',monospace;letter-spacing:0.04em}
  .a-pill-ok{background:#1c5448;color:#7ecfb0}
  .a-pill-warn{background:#5a4a1c;color:#e6cb7a}
  .a-pill-err{background:#5a1c1c;color:#e67a7a}
  .a-pager{display:flex;justify-content:space-between;align-items:center;margin-top:16px;font-size:13px;color:#7aad98}
  .a-pager button{background:#13201a;border:1px solid #1c2b25;color:#7aad98;border-radius:6px;padding:6px 12px;font-size:12px;cursor:pointer;font-family:inherit}
  .a-pager button:disabled{opacity:0.4;cursor:not-allowed}
  .a-empty{padding:40px;text-align:center;color:#5a8a78;font-style:italic}
  .a-loading{padding:40px;text-align:center;color:#5a8a78}
  .a-json{font-family:'DM Mono',monospace;font-size:11px;color:#7aad98;max-width:400px;white-space:pre-wrap;word-break:break-all}
  .a-back{background:none;border:none;color:#7aad98;cursor:pointer;font-family:'DM Mono',monospace;font-size:12px;letter-spacing:0.06em;margin-bottom:16px;padding:0}
  .a-back:hover{color:#cfe6da}
  .a-msg{padding:14px 16px;border-radius:10px;margin-bottom:10px;border:1px solid #1c2b25}
  .a-msg-user{background:#1c2b25}
  .a-msg-asst{background:#13201a}
  .a-msg-meta{font-size:10px;color:#5a8a78;letter-spacing:0.08em;text-transform:uppercase;margin-bottom:6px;font-family:'DM Mono',monospace}
  .a-section{margin-bottom:32px}
  .a-section-h{font-family:'Playfair Display',serif;font-size:18px;color:#cfe6da;margin:0 0 12px}
  .a-login{min-height:100vh;display:flex;align-items:center;justify-content:center;flex-direction:column;padding:24px;background:#0e1410}
  .a-login-box{background:#13201a;border:1px solid #1c2b25;border-radius:14px;padding:36px;width:100%;max-width:380px}
  .a-login h1{font-family:'Playfair Display',serif;font-size:26px;color:#7ecfb0;margin:0 0 8px;font-weight:700}
  .a-login p{font-size:13px;color:#7aad98;margin:0 0 24px}
  .a-login input{width:100%;background:#0a100c;border:1px solid #1c2b25;color:#cfe6da;border-radius:8px;padding:11px 14px;font-size:14px;font-family:'DM Mono',monospace;letter-spacing:0.06em}
  .a-login input:focus{outline:none;border-color:#2d7a62}
  .a-login button{width:100%;margin-top:12px;background:#2d7a62;color:#cfe6da;border:none;border-radius:8px;padding:12px;font-size:14px;font-weight:600;cursor:pointer;font-family:inherit}
  .a-login-err{color:#e67a7a;font-size:12px;margin-top:10px;font-family:'DM Mono',monospace}
  .a-signout-wrap{border-top:1px solid #1c2b25;margin:16px 0 0;padding-top:12px}
  .a-tier-row{background:#13201a;border:1px solid #1c2b25;border-radius:12px;padding:14px 18px;margin-bottom:10px;display:flex;align-items:center;gap:14px}
  .a-tier-btns{display:flex;gap:4px;flex-shrink:0}
  .a-tier-btn{border:1px solid #1c2b25;border-radius:6px;padding:5px 13px;font-size:12px;font-family:'DM Mono',monospace;cursor:pointer;letter-spacing:0.05em;transition:all .12s;background:#0a100c;color:#5a8a78}
  .a-tier-btn.active-haiku{background:#1c5448;border-color:#2d7a62;color:#7ecfb0}
  .a-tier-btn.active-sonnet{background:#4a3a1a;border-color:#8a6a2a;color:#e6c97a}
  .a-tier-btn.active-opus{background:#3a1a4a;border-color:#7a2a8a;color:#d07ae6}
  .a-tier-group-h{font-size:11px;color:#5a8a78;letter-spacing:0.12em;text-transform:uppercase;font-family:'DM Mono',monospace;margin:20px 0 8px}

  @media(max-width:860px){
    .a-shell{flex-direction:column;min-height:100vh}
    .a-side{width:100%;border-right:none;border-bottom:1px solid #1c2b25;padding:12px 0;position:sticky;top:0;z-index:10}
    .a-logo{padding:0 14px 10px;margin-bottom:8px}
    .a-nav{flex-direction:row;overflow-x:auto;padding:0 12px;gap:6px;-webkit-overflow-scrolling:touch}
    .a-nav::-webkit-scrollbar{display:none}
    .a-nav button{white-space:nowrap;padding:7px 12px;font-size:12px;flex-shrink:0}
    .a-signout-wrap{border-top:none;border-left:1px solid #1c2b25;margin:0 0 0 6px;padding:0 0 0 10px;flex-shrink:0}
    .a-main{padding:18px 14px;width:100%}
    .a-h{flex-wrap:wrap;gap:10px;margin-bottom:16px}
    .a-h h1{font-size:22px}
    .a-cards{grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:10px;margin-bottom:24px}
    .a-card{padding:14px}
    .a-card-v{font-size:24px}
    .a-filters{padding:10px;gap:8px}
    .a-filters input,.a-filters select{padding:8px 10px;font-size:16px}
    .a-table th,.a-table td{padding:9px 10px;font-size:12px}
    .a-section-h{font-size:16px}
    .a-login-box{padding:24px}
    .a-login input{font-size:16px}
  }
  @media(max-width:520px){
    .a-main{padding:14px 10px}
    .a-h h1{font-size:19px}
    .a-h{margin-bottom:12px}
  }
`;

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmtDate = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  return d.toLocaleString("en-US", { year: "2-digit", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

const fmtRelative = (s) => {
  if (!s) return "—";
  const diff = Date.now() - new Date(s).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

const statusPill = (code) => {
  if (code == null) return null;
  const cls = code >= 500 ? "a-pill-err" : code >= 400 ? "a-pill-warn" : "a-pill-ok";
  return <span className={`a-pill ${cls}`}>{code}</span>;
};

const downloadCsv = (rows, filename) => {
  if (!rows.length) return;
  const cols = Object.keys(rows[0]);
  const escape = (v) => {
    if (v == null) return "";
    const s = typeof v === "object" ? JSON.stringify(v) : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => escape(r[c])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

// ─── Date range ──────────────────────────────────────────────────────────────
const RANGE_PRESETS = [
  { id: "1d", label: "24h", ms: 24 * 60 * 60 * 1000 },
  { id: "7d", label: "7d",  ms: 7 * 24 * 60 * 60 * 1000 },
  { id: "30d", label: "30d", ms: 30 * 24 * 60 * 60 * 1000 },
  { id: "all", label: "all", ms: null },
];

const DateRange = ({ value, onChange }) => (
  <>
    <span className="a-filter-l">Range</span>
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      {RANGE_PRESETS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
    </select>
  </>
);

const rangeToFrom = (id) => {
  const p = RANGE_PRESETS.find((r) => r.id === id);
  if (!p?.ms) return undefined;
  return new Date(Date.now() - p.ms).toISOString();
};

// ─── Generic table view ──────────────────────────────────────────────────────
const Pager = ({ page, pageSize, total, onPage }) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="a-pager">
      <div>{total.toLocaleString()} rows · page {page} of {totalPages}</div>
      <div style={{ display: "flex", gap: 6 }}>
        <button disabled={page <= 1} onClick={() => onPage(page - 1)}>← prev</button>
        <button disabled={page >= totalPages} onClick={() => onPage(page + 1)}>next →</button>
      </div>
    </div>
  );
};

// ─── Summary view ────────────────────────────────────────────────────────────
function Summary() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    apiCall("summary").then(setData).catch((e) => setErr(e.message));
  }, []);
  if (err) return <div className="a-empty">Error: {err}</div>;
  if (!data) return <div className="a-loading">Loading…</div>;
  const cards = [
    ["Users total",       data.users_total,         null],
    ["Signups",           data.signups_24h,         "last 24h"],
    ["Logins",            data.logins_24h,          "last 24h"],
    ["Logins",            data.logins_7d,           "last 7d"],
    ["Chats",             data.chats_24h,           "last 24h"],
    ["Chats",             data.chats_7d,            "last 7d"],
    ["Property lookups",  data.property_lookups_24h,"last 24h"],
    ["Errors",            data.errors_24h,          "last 24h"],
  ];
  return (
    <>
      <div className="a-h"><h1>Summary</h1></div>
      <div className="a-cards">
        {cards.map(([l, v, s], i) => (
          <div className="a-card" key={i}>
            <div className="a-card-l">{l}</div>
            <div className="a-card-v">{(v ?? 0).toLocaleString()}</div>
            {s && <div className="a-card-s">{s}</div>}
          </div>
        ))}
      </div>
      {data.top_countries?.length > 0 && (
        <div className="a-section">
          <div className="a-section-h">Top countries (last 7d)</div>
          <table className="a-table">
            <thead><tr><th>Country</th><th>Logins</th></tr></thead>
            <tbody>
              {data.top_countries.map((c) => (
                <tr key={c.country}><td className="a-mono">{c.country}</td><td>{c.count}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ─── Events view ─────────────────────────────────────────────────────────────
function Events() {
  const [range, setRange] = useState("1d");
  const [name, setName] = useState("");
  const [q, setQ] = useState("");
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ rows: [], total: 0 });
  const [auto, setAuto] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    apiCall("events", {
      from: rangeToFrom(range),
      name: name || undefined,
      q: q || undefined,
      status_min: errorsOnly ? 500 : undefined,
      page,
      pageSize: 50,
    }).then(setData).finally(() => setLoading(false));
  }, [range, name, q, errorsOnly, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!auto) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [auto, load]);

  return (
    <>
      <div className="a-h">
        <h1>Events</h1>
        <div style={{ display: "flex", gap: 10 }}>
          <button className={`a-btn ${auto ? "" : "a-btn-ghost"}`} onClick={() => setAuto(!auto)}>
            {auto ? "● live" : "○ live"}
          </button>
          <button className="a-btn a-btn-ghost" onClick={() => downloadCsv(data.rows, "events.csv")}>Export CSV</button>
        </div>
      </div>
      <div className="a-filters">
        <DateRange value={range} onChange={(v) => { setRange(v); setPage(1); }} />
        <span className="a-filter-l">Name</span>
        <input value={name} onChange={(e) => { setName(e.target.value); setPage(1); }} placeholder="e.g. chat_message_sent" style={{ width: 200 }} />
        <span className="a-filter-l">Search</span>
        <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="ip / name" style={{ width: 160 }} />
        <label style={{ fontSize: 12, color: "#7aad98", display: "flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={errorsOnly} onChange={(e) => { setErrorsOnly(e.target.checked); setPage(1); }} />
          errors only
        </label>
      </div>
      {loading && data.rows.length === 0 ? <div className="a-loading">Loading…</div> : (
        <>
          <table className="a-table">
            <thead><tr>
              <th>Time</th><th>Name</th><th>Status</th><th>User</th><th>IP</th><th>Latency</th><th>Payload</th>
            </tr></thead>
            <tbody>
              {data.rows.length === 0 && <tr><td colSpan={7} className="a-empty">No events</td></tr>}
              {data.rows.map((r) => (
                <tr key={r.id}>
                  <td className="a-mono" title={r.created_at}>{fmtRelative(r.created_at)}</td>
                  <td className="a-mono">{r.name}</td>
                  <td>{statusPill(r.status_code)}</td>
                  <td className="a-mono">{r.user_id ? r.user_id.slice(0, 8) : "—"}</td>
                  <td className="a-mono">{r.ip || "—"}</td>
                  <td className="a-mono">{r.latency_ms != null ? r.latency_ms + "ms" : "—"}</td>
                  <td className="a-json">{r.payload && Object.keys(r.payload).length ? JSON.stringify(r.payload) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={page} pageSize={50} total={data.total} onPage={setPage} />
        </>
      )}
    </>
  );
}

// ─── Logins view ─────────────────────────────────────────────────────────────
function Logins() {
  const [range, setRange] = useState("7d");
  const [country, setCountry] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    apiCall("logins", { from: rangeToFrom(range), country: country || undefined, q: q || undefined, page, pageSize: 50 })
      .then(setData).finally(() => setLoading(false));
  }, [range, country, q, page]);
  return (
    <>
      <div className="a-h">
        <h1>Logins</h1>
        <button className="a-btn a-btn-ghost" onClick={() => downloadCsv(data.rows, "logins.csv")}>Export CSV</button>
      </div>
      <div className="a-filters">
        <DateRange value={range} onChange={(v) => { setRange(v); setPage(1); }} />
        <span className="a-filter-l">Country</span>
        <input value={country} onChange={(e) => { setCountry(e.target.value); setPage(1); }} placeholder="US, IN…" style={{ width: 80 }} />
        <span className="a-filter-l">Search</span>
        <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="email / city / IP" style={{ width: 200 }} />
      </div>
      {loading && data.rows.length === 0 ? <div className="a-loading">Loading…</div> : (
        <>
          <table className="a-table">
            <thead><tr>
              <th>Time</th><th>Email</th><th>Country</th><th>City</th><th>IP</th><th>Browser</th><th>OS</th><th>Device</th><th>UTM</th>
            </tr></thead>
            <tbody>
              {data.rows.length === 0 && <tr><td colSpan={9} className="a-empty">No logins</td></tr>}
              {data.rows.map((r) => (
                <tr key={r.id}>
                  <td className="a-mono" title={r.created_at}>{fmtRelative(r.created_at)}</td>
                  <td className="a-mono">{r.email || "—"}</td>
                  <td className="a-mono">{r.country || "—"}</td>
                  <td>{r.city || "—"}</td>
                  <td className="a-mono">{r.ip || "—"}</td>
                  <td>{r.browser ? `${r.browser} ${r.browser_version || ""}`.trim() : "—"}</td>
                  <td>{r.os ? `${r.os} ${r.os_version || ""}`.trim() : "—"}</td>
                  <td className="a-mono">{r.device_type || "—"}</td>
                  <td className="a-mono" style={{ fontSize: 11 }}>{r.utm_source || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={page} pageSize={50} total={data.total} onPage={setPage} />
        </>
      )}
    </>
  );
}

// ─── Unique visitors view ────────────────────────────────────────────────────
function Unique() {
  const [range, setRange] = useState("7d");
  const [groupBy, setGroupBy] = useState("ip");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ rows: [], total: 0, total_events_scanned: 0 });
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    apiCall("unique", { from: rangeToFrom(range), groupBy, q: q || undefined, page, pageSize: 50 })
      .then(setData).finally(() => setLoading(false));
  }, [range, groupBy, q, page]);
  return (
    <>
      <div className="a-h">
        <h1>Unique visitors</h1>
        <button className="a-btn a-btn-ghost" onClick={() => downloadCsv(data.rows, `unique_${groupBy}.csv`)}>Export CSV</button>
      </div>
      <div className="a-cards">
        <div className="a-card"><div className="a-card-l">Distinct {groupBy === "ip" ? "IPs" : "emails"}</div><div className="a-card-v">{data.total}</div></div>
        <div className="a-card"><div className="a-card-l">Login events scanned</div><div className="a-card-v">{data.total_events_scanned}</div></div>
      </div>
      <div className="a-filters">
        <DateRange value={range} onChange={(v) => { setRange(v); setPage(1); }} />
        <span className="a-filter-l">Group by</span>
        <select value={groupBy} onChange={(e) => { setGroupBy(e.target.value); setPage(1); }}>
          <option value="ip">IP</option>
          <option value="email">Email</option>
        </select>
        <span className="a-filter-l">Search</span>
        <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="ip / email / city / country" style={{ width: 240 }} />
      </div>
      {loading && data.rows.length === 0 ? <div className="a-loading">Loading…</div> : (
        <>
          <table className="a-table">
            <thead><tr>
              <th>{groupBy === "ip" ? "IP" : "Email"}</th>
              <th>Logins</th>
              <th>Country</th>
              <th>City</th>
              <th>Latest browser</th>
              <th>Device</th>
              <th>{groupBy === "ip" ? "Emails seen" : "IPs seen"}</th>
              <th>First seen</th>
              <th>Last seen</th>
            </tr></thead>
            <tbody>
              {data.rows.length === 0 && <tr><td colSpan={9} className="a-empty">No visitors</td></tr>}
              {data.rows.map((r) => (
                <tr key={r.key}>
                  <td className="a-mono">{r.key}</td>
                  <td>{r.logins}</td>
                  <td className="a-mono">{r.country || "—"}</td>
                  <td>{r.city || "—"}</td>
                  <td>{r.latest_browser || "—"}</td>
                  <td className="a-mono">{r.latest_device || "—"}</td>
                  <td className="a-mono" style={{ fontSize: 11 }} title={(groupBy === "ip" ? r.emails_list : r.ips_list).join(", ")}>
                    {groupBy === "ip" ? r.distinct_emails : r.distinct_ips}
                  </td>
                  <td className="a-mono">{fmtRelative(r.first_seen)}</td>
                  <td className="a-mono">{fmtRelative(r.last_seen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={page} pageSize={50} total={data.total} onPage={setPage} />
        </>
      )}
    </>
  );
}

// ─── Users view ──────────────────────────────────────────────────────────────
function Users({ onPick }) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    apiCall("users", { q: q || undefined, page, pageSize: 50 }).then(setData).finally(() => setLoading(false));
  }, [q, page]);
  return (
    <>
      <div className="a-h">
        <h1>Users</h1>
        <button className="a-btn a-btn-ghost" onClick={() => downloadCsv(data.rows, "users.csv")}>Export CSV</button>
      </div>
      <div className="a-filters">
        <span className="a-filter-l">Search</span>
        <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="email or name" style={{ width: 240 }} />
      </div>
      {loading && data.rows.length === 0 ? <div className="a-loading">Loading…</div> : (
        <>
          <table className="a-table">
            <thead><tr>
              <th>Joined</th><th>Email</th><th>Name</th><th>Role</th><th>Logins</th><th>Chats</th><th>Lookups</th>
            </tr></thead>
            <tbody>
              {data.rows.length === 0 && <tr><td colSpan={7} className="a-empty">No users</td></tr>}
              {data.rows.map((r) => (
                <tr key={r.id} className="clickable" onClick={() => onPick(r.id)}>
                  <td className="a-mono">{fmtDate(r.created_at)}</td>
                  <td className="a-mono">{r.email}</td>
                  <td>{r.name || "—"}</td>
                  <td className="a-mono">{r.role}</td>
                  <td>{r.logins ?? 0}</td>
                  <td>{r.chats ?? 0}</td>
                  <td>{r.lookups ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={page} pageSize={50} total={data.total} onPage={setPage} />
        </>
      )}
    </>
  );
}

// ─── User detail ─────────────────────────────────────────────────────────────
function UserDetail({ id, onBack, onPickChat }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    apiCall("user", { id }).then(setData).catch((e) => setErr(e.message));
  }, [id]);
  if (err) return <><button className="a-back" onClick={onBack}>← back</button><div className="a-empty">Error: {err}</div></>;
  if (!data) return <><button className="a-back" onClick={onBack}>← back</button><div className="a-loading">Loading…</div></>;
  const u = data.user;
  return (
    <>
      <button className="a-back" onClick={onBack}>← back to users</button>
      <div className="a-h"><h1>{u.email}</h1></div>
      <div className="a-cards">
        <div className="a-card"><div className="a-card-l">Joined</div><div className="a-card-v" style={{ fontSize: 18 }}>{fmtDate(u.created_at)}</div></div>
        <div className="a-card"><div className="a-card-l">Role</div><div className="a-card-v" style={{ fontSize: 18 }}>{u.role}</div></div>
        <div className="a-card"><div className="a-card-l">Logins</div><div className="a-card-v">{data.recent_logins.length}</div></div>
        <div className="a-card"><div className="a-card-l">Chats</div><div className="a-card-v">{data.recent_chats.length}</div></div>
      </div>
      <div className="a-section">
        <div className="a-section-h">Recent logins</div>
        <table className="a-table">
          <thead><tr><th>Time</th><th>Country</th><th>City</th><th>IP</th><th>Browser</th><th>Device</th></tr></thead>
          <tbody>
            {data.recent_logins.length === 0 && <tr><td colSpan={6} className="a-empty">No logins</td></tr>}
            {data.recent_logins.slice(0, 20).map((l) => (
              <tr key={l.id}>
                <td className="a-mono">{fmtRelative(l.created_at)}</td>
                <td className="a-mono">{l.country || "—"}</td>
                <td>{l.city || "—"}</td>
                <td className="a-mono">{l.ip || "—"}</td>
                <td>{l.browser || "—"}</td>
                <td className="a-mono">{l.device_type || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="a-section">
        <div className="a-section-h">Chat sessions</div>
        <table className="a-table">
          <thead><tr><th>Time</th><th>Mode</th><th>Stage</th></tr></thead>
          <tbody>
            {data.recent_chats.length === 0 && <tr><td colSpan={3} className="a-empty">No chats</td></tr>}
            {data.recent_chats.map((c) => (
              <tr key={c.id} className="clickable" onClick={() => onPickChat(c.id)}>
                <td className="a-mono">{fmtRelative(c.created_at)}</td>
                <td className="a-mono">{c.mode || "—"}</td>
                <td className="a-mono">{c.stage || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="a-section">
        <div className="a-section-h">Property lookups</div>
        <table className="a-table">
          <thead><tr><th>Time</th><th>Address</th><th>Cache hit</th><th>Latency</th></tr></thead>
          <tbody>
            {data.recent_lookups.length === 0 && <tr><td colSpan={4} className="a-empty">No lookups</td></tr>}
            {data.recent_lookups.map((l, i) => (
              <tr key={i}>
                <td className="a-mono">{fmtRelative(l.created_at)}</td>
                <td>{l.payload?.address || "—"}</td>
                <td>{l.payload?.cache_hit ? <span className="a-pill a-pill-ok">hit</span> : "—"}</td>
                <td className="a-mono">{l.latency_ms != null ? l.latency_ms + "ms" : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ─── Chats list ──────────────────────────────────────────────────────────────
function Chats({ onPick }) {
  const [range, setRange] = useState("7d");
  const [mode, setMode] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    apiCall("chats", { from: rangeToFrom(range), mode: mode || undefined, page, pageSize: 50 })
      .then(setData).finally(() => setLoading(false));
  }, [range, mode, page]);
  return (
    <>
      <div className="a-h">
        <h1>Chat sessions</h1>
        <button className="a-btn a-btn-ghost" onClick={() => downloadCsv(data.rows, "chats.csv")}>Export CSV</button>
      </div>
      <div className="a-filters">
        <DateRange value={range} onChange={(v) => { setRange(v); setPage(1); }} />
        <span className="a-filter-l">Mode</span>
        <select value={mode} onChange={(e) => { setMode(e.target.value); setPage(1); }}>
          <option value="">all</option>
          <option value="listing">listing</option>
          <option value="preferences">preferences</option>
          <option value="seller">seller</option>
          <option value="offer">offer</option>
        </select>
      </div>
      {loading && data.rows.length === 0 ? <div className="a-loading">Loading…</div> : (
        <>
          <table className="a-table">
            <thead><tr>
              <th>Time</th><th>Mode</th><th>Stage</th><th>Messages</th><th>Tokens</th><th>First message</th>
            </tr></thead>
            <tbody>
              {data.rows.length === 0 && <tr><td colSpan={6} className="a-empty">No sessions</td></tr>}
              {data.rows.map((r) => (
                <tr key={r.id} className="clickable" onClick={() => onPick(r.id)}>
                  <td className="a-mono">{fmtRelative(r.created_at)}</td>
                  <td className="a-mono">{r.mode || "—"}</td>
                  <td className="a-mono">{r.stage || "—"}</td>
                  <td>{r.message_count}</td>
                  <td>{r.total_tokens.toLocaleString()}</td>
                  <td style={{ maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.first_message || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={page} pageSize={50} total={data.total} onPage={setPage} />
        </>
      )}
    </>
  );
}

// ─── Chat detail ─────────────────────────────────────────────────────────────
function ChatDetail({ id, onBack }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    apiCall("chat", { id }).then(setData).catch((e) => setErr(e.message));
  }, [id]);
  if (err) return <><button className="a-back" onClick={onBack}>← back</button><div className="a-empty">Error: {err}</div></>;
  if (!data) return <><button className="a-back" onClick={onBack}>← back</button><div className="a-loading">Loading…</div></>;
  const total = data.messages.reduce((s, m) => s + (m.tokens_in || 0) + (m.tokens_out || 0), 0);
  return (
    <>
      <button className="a-back" onClick={onBack}>← back</button>
      <div className="a-h">
        <h1>Session</h1>
        <div className="a-mono" style={{ color: "#7aad98", fontSize: 12 }}>
          {data.session.mode || "—"} · {data.session.stage || "—"} · {data.messages.length} msg · {total.toLocaleString()} tokens
        </div>
      </div>
      {data.messages.map((m) => (
        <div key={m.id} className={`a-msg ${m.role === "user" ? "a-msg-user" : "a-msg-asst"}`}>
          <div className="a-msg-meta">
            {m.role} · {fmtDate(m.created_at)}
            {m.tokens_in != null && ` · in ${m.tokens_in}`}
            {m.tokens_out != null && ` · out ${m.tokens_out}`}
            {m.model && ` · ${m.model}`}
          </div>
          <div style={{ whiteSpace: "pre-wrap", fontSize: 14, color: "#cfe6da" }}>{m.content}</div>
        </div>
      ))}
    </>
  );
}

// ─── Properties view ─────────────────────────────────────────────────────────
function Properties() {
  const [range, setRange] = useState("7d");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ rows: [], total: 0, overall_cache_hit_rate: 0, total_lookups: 0 });
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    apiCall("properties", { from: rangeToFrom(range), q: q || undefined, page, pageSize: 50 })
      .then(setData).finally(() => setLoading(false));
  }, [range, q, page]);
  return (
    <>
      <div className="a-h">
        <h1>Property lookups</h1>
        <button className="a-btn a-btn-ghost" onClick={() => downloadCsv(data.rows, "properties.csv")}>Export CSV</button>
      </div>
      <div className="a-cards">
        <div className="a-card"><div className="a-card-l">Lookups</div><div className="a-card-v">{data.total_lookups.toLocaleString()}</div></div>
        <div className="a-card"><div className="a-card-l">Cache hit rate</div><div className="a-card-v">{data.overall_cache_hit_rate}%</div></div>
        <div className="a-card"><div className="a-card-l">Unique addresses</div><div className="a-card-v">{data.total}</div></div>
      </div>
      <div className="a-filters">
        <DateRange value={range} onChange={(v) => { setRange(v); setPage(1); }} />
        <span className="a-filter-l">Search</span>
        <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="address" style={{ width: 280 }} />
      </div>
      {loading && data.rows.length === 0 ? <div className="a-loading">Loading…</div> : (
        <>
          <table className="a-table">
            <thead><tr>
              <th>Address</th><th>Lookups</th><th>Unique users</th><th>Cache hit %</th><th>P50</th><th>P95</th>
            </tr></thead>
            <tbody>
              {data.rows.length === 0 && <tr><td colSpan={6} className="a-empty">No lookups</td></tr>}
              {data.rows.map((r) => (
                <tr key={r.address}>
                  <td>{r.address}</td>
                  <td>{r.count}</td>
                  <td>{r.unique_users}</td>
                  <td>{r.cache_hit_rate}%</td>
                  <td className="a-mono">{r.p50_ms != null ? r.p50_ms + "ms" : "—"}</td>
                  <td className="a-mono">{r.p95_ms != null ? r.p95_ms + "ms" : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={page} pageSize={50} total={data.total} onPage={setPage} />
        </>
      )}
    </>
  );
}

// ─── Vendors view ────────────────────────────────────────────────────────────
const VENDOR_TYPES = [
  "stager","handyman","painter","cleaner","landscaper",
  "electrician","plumber","hvac","roofer","general_contractor",
  "photographer","videographer",
  "inspector","pest_inspector","appraiser",
  "lender","real_estate_attorney","notary","title_escrow",
  "listing_assistant",
  "mover","storage",
  "interior_designer",
];

function Vendors() {
  const [data, setData] = useState(null);
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null); // null | "new" | vendor object
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const reload = useCallback(() => {
    apiCall("vendors", { type, status, q, page, pageSize: 50 })
      .then(setData)
      .catch((e) => setErr(e.message));
  }, [type, status, q, page]);

  useEffect(() => { reload(); }, [reload]);

  const save = async (fields) => {
    setBusy(true); setErr(null);
    try {
      await apiCall("vendors", fields, { method: "POST" });
      setEditing(null);
      reload();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Suspend this vendor? It won't show up in the marketplace.")) return;
    try {
      await apiCall("vendors", { id, action: "delete" }, { method: "POST" });
      reload();
    } catch (e) { setErr(e.message); }
  };

  const setVendorStatus = async (v, newStatus) => {
    try {
      await apiCall("vendors", { ...v, status: newStatus }, { method: "POST" });
      reload();
    } catch (e) { setErr(e.message); }
  };

  return (
    <>
      <div className="a-h">
        <h1>Vendors</h1>
        <button className="a-btn" onClick={() => setEditing("new")}>+ Add vendor</button>
      </div>

      <div className="a-filters">
        <input placeholder="Search name / blurb…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
        <select value={type} onChange={(e) => { setType(e.target.value); setPage(1); }}>
          <option value="">All types</option>
          {VENDOR_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
          <option value="">All status</option>
          <option value="active">active</option>
          <option value="draft">draft</option>
          <option value="suspended">suspended</option>
        </select>
        <span style={{ marginLeft: "auto", fontFamily: "'DM Mono',monospace", fontSize: 11, color: "#5a8a78" }}>
          {data ? `${data.total} vendors` : ""}
        </span>
      </div>

      {err && <div className="a-empty" style={{ color: "#e67a7a", padding: 12 }}>Error: {err}</div>}

      {editing && (
        <VendorForm
          vendor={editing === "new" ? null : editing}
          onSave={save}
          onCancel={() => setEditing(null)}
          busy={busy}
        />
      )}

      {!data && !err && <div className="a-loading">Loading…</div>}

      {data && data.rows.length === 0 && <div className="a-empty">No vendors match.</div>}

      {data && data.rows.length > 0 && (
        <>
          <table className="a-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Rating</th>
                <th>Lead</th>
                <th>ZIPs</th>
                <th>Status</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((v) => (
                <tr key={v.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{v.name}</div>
                    {v.blurb && <div style={{ fontSize: 11, color: "#7aad98", marginTop: 2, lineHeight: 1.4 }}>{v.blurb}</div>}
                  </td>
                  <td className="a-mono">{v.type}</td>
                  <td className="a-mono">{v.rating != null ? `${v.rating} ★ (${v.review_count || 0})` : "—"}</td>
                  <td className="a-mono">{v.lead_time_days != null ? `${v.lead_time_days}d` : "—"}</td>
                  <td className="a-mono" style={{ maxWidth: 160, fontSize: 11 }}>
                    {(v.service_area_zips || []).slice(0, 4).join(", ")}{(v.service_area_zips || []).length > 4 ? "…" : ""}
                  </td>
                  <td>
                    <span className={`a-pill ${v.status === "active" ? "a-pill-ok" : v.status === "suspended" ? "a-pill-err" : "a-pill-warn"}`}>
                      {v.status}
                    </span>
                  </td>
                  <td className="a-mono" style={{ fontSize: 11 }}>{new Date(v.created_at).toLocaleDateString()}</td>
                  <td style={{ whiteSpace: "nowrap", display: "flex", gap: 6, alignItems: "center", flexWrap: "nowrap" }}>
                    {v.status !== "active" && (
                      <button className="a-btn" style={{ padding: "5px 10px", fontSize: 11, background: "#1c5448" }} onClick={() => setVendorStatus(v, "active")}>✓ Approve</button>
                    )}
                    {v.status !== "suspended" && (
                      <button className="a-btn-ghost" style={{ padding: "5px 9px", fontSize: 11, color: "#e67a7a" }} onClick={() => remove(v.id)}>✕ Suspend</button>
                    )}
                    {v.status !== "draft" && (
                      <button className="a-btn-ghost" style={{ padding: "5px 9px", fontSize: 11, color: "#e6cb7a" }} onClick={() => setVendorStatus(v, "draft")}>↩ Draft</button>
                    )}
                    <button className="a-btn-ghost" style={{ padding: "5px 9px", fontSize: 11 }} onClick={() => setEditing(v)}>edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={page} pageSize={50} total={data.total} onPage={setPage} />
        </>
      )}
    </>
  );
}

function VendorForm({ vendor, onSave, onCancel, busy }) {
  const [f, setF] = useState({
    id: vendor?.id,
    type: vendor?.type || "stager",
    name: vendor?.name || "",
    blurb: vendor?.blurb || "",
    contact_email: vendor?.contact_email || "",
    contact_phone: vendor?.contact_phone || "",
    base_pricing: JSON.stringify(vendor?.base_pricing || {}, null, 2),
    service_area_zips: (vendor?.service_area_zips || []).join(", "),
    rating: vendor?.rating ?? "",
    lead_time_days: vendor?.lead_time_days ?? "",
    status: vendor?.status || "active",
  });
  const [parseErr, setParseErr] = useState(null);

  const update = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    setParseErr(null);
    let parsed;
    try { parsed = JSON.parse(f.base_pricing || "{}"); }
    catch { setParseErr("Base pricing must be valid JSON"); return; }
    onSave({
      id: f.id,
      type: f.type,
      name: f.name.trim(),
      blurb: f.blurb.trim() || null,
      contact_email: f.contact_email.trim() || null,
      contact_phone: f.contact_phone.trim() || null,
      base_pricing: parsed,
      service_area_zips: f.service_area_zips.split(",").map((s) => s.trim()).filter(Boolean),
      rating: f.rating === "" ? null : Number(f.rating),
      lead_time_days: f.lead_time_days === "" ? null : Number(f.lead_time_days),
      status: f.status,
    });
  };

  const inputStyle = { width: "100%", background: "#0a100c", border: "1px solid #1c2b25", color: "#cfe6da", borderRadius: 6, padding: "8px 10px", fontSize: 13, fontFamily: "inherit" };
  const labelStyle = { display: "block", fontSize: 10, color: "#5a8a78", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 };

  return (
    <form onSubmit={submit} style={{ background: "#13201a", border: "1px solid #1c2b25", borderRadius: 12, padding: 18, marginBottom: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, color: "#cfe6da", margin: 0 }}>
          {f.id ? "Edit vendor" : "Add vendor"}
        </h3>
        <button type="button" className="a-btn-ghost" onClick={onCancel} style={{ padding: "5px 10px", fontSize: 11 }}>cancel</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        <div>
          <label style={labelStyle}>Type</label>
          <select value={f.type} onChange={(e) => update("type", e.target.value)} style={inputStyle}>
            {VENDOR_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Status</label>
          <select value={f.status} onChange={(e) => update("status", e.target.value)} style={inputStyle}>
            <option value="active">active</option>
            <option value="draft">draft</option>
            <option value="suspended">suspended</option>
          </select>
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={labelStyle}>Name *</label>
          <input value={f.name} onChange={(e) => update("name", e.target.value)} required style={inputStyle} />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={labelStyle}>Blurb (1-2 sentences)</label>
          <input value={f.blurb} onChange={(e) => update("blurb", e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Contact email</label>
          <input value={f.contact_email} onChange={(e) => update("contact_email", e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Contact phone</label>
          <input value={f.contact_phone} onChange={(e) => update("contact_phone", e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Rating (0-5)</label>
          <input type="number" min="0" max="5" step="0.1" value={f.rating} onChange={(e) => update("rating", e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Lead time (days)</label>
          <input type="number" min="0" value={f.lead_time_days} onChange={(e) => update("lead_time_days", e.target.value)} style={inputStyle} />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={labelStyle}>Service area ZIPs (comma-separated)</label>
          <input value={f.service_area_zips} onChange={(e) => update("service_area_zips", e.target.value)} placeholder="94110, 94114, 94117" style={inputStyle} />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={labelStyle}>Base pricing (JSON)</label>
          <textarea
            value={f.base_pricing}
            onChange={(e) => update("base_pricing", e.target.value)}
            style={{ ...inputStyle, minHeight: 100, fontFamily: "'DM Mono',monospace", fontSize: 12 }}
            placeholder='{ "single_family": 525, "condo": 425 }'
          />
          {parseErr && <div style={{ color: "#e67a7a", fontSize: 11, marginTop: 4 }}>{parseErr}</div>}
        </div>
      </div>

      <button type="submit" className="a-btn" disabled={busy || !f.name.trim()}>
        {busy ? "Saving…" : f.id ? "Update vendor" : "Create vendor"}
      </button>
    </form>
  );
}

// ─── Settings view ───────────────────────────────────────────────────────────
const FLAGS = [
  { key: "force_mock_listings", label: "Force mock listings",  desc: "Return mock Bay Area listings even when RapidAPI is configured. Useful for demos and saving API quota." },
  { key: "disable_ai",          label: "Disable AI assistant", desc: "Pause all AI calls. /api/chat returns a 'temporarily unavailable' message. Saves token spend during outages." },
];

const MODEL_TIERS = ["haiku", "sonnet", "opus"];
const TIER_LABELS = { haiku: "Haiku", sonnet: "Sonnet", opus: "Opus" };
const TIER_DESC = { haiku: "cheapest", sonnet: "balanced", opus: "most capable" };

const TASK_GROUPS = [
  { group: "Chat", tasks: [
    { key: "model_chat",           label: "Chat assistant",       codeDefault: "haiku" },
  ]},
  { group: "Vendor", tasks: [
    { key: "model_vendor_quote",   label: "Quote generation",     codeDefault: "haiku" },
    { key: "model_vendor_ranking", label: "Vendor ranking",       codeDefault: "haiku" },
    { key: "model_vendor_pricing", label: "Pricing parse",        codeDefault: "haiku" },
    { key: "model_vendor_counter", label: "Counter-quote draft",  codeDefault: "haiku" },
  ]},
  { group: "Listing", tasks: [
    { key: "model_listing_copy",    label: "Listing copy",         codeDefault: "haiku" },
    { key: "model_prep_recs",       label: "Prep recommendations", codeDefault: "haiku" },
    { key: "model_open_house_copy", label: "Open house copy",      codeDefault: "haiku" },
    { key: "model_disclosures_qs",  label: "Disclosure questions", codeDefault: "haiku" },
    { key: "model_valuation",       label: "Valuation",            codeDefault: "haiku" },
  ]},
  { group: "Documents", tasks: [
    { key: "model_doc_extraction",   label: "Document extraction", codeDefault: "sonnet" },
    { key: "model_inspection_parse", label: "Inspection parsing",  codeDefault: "sonnet" },
  ]},
  { group: "Negotiation", tasks: [
    { key: "model_offer_strategy", label: "Offer strategy", codeDefault: "sonnet" },
  ]},
];

function Settings() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const reload = () => apiCall("settings").then(setData).catch((e) => setErr(e.message));
  useEffect(() => { reload(); }, []);

  const toggle = async (key, current) => {
    setBusy(key); setErr(null);
    try {
      const next = await apiCall("settings", { key, value: !current }, { method: "POST" });
      setData(next);
    } catch (e) { setErr(e.message); }
    setBusy(null);
  };

  const setTier = async (key, tier) => {
    setBusy(key); setErr(null);
    try {
      const next = await apiCall("settings", { key, value: tier || null }, { method: "POST" });
      setData(next);
    } catch (e) { setErr(e.message); }
    setBusy(null);
  };

  if (err) return <div className="a-empty">Error: {err}</div>;
  if (!data) return <div className="a-loading">Loading…</div>;

  return (
    <>
      <div className="a-h">
        <h1>Settings</h1>
        <span style={{ fontSize: 11, color: "#5a8a78", fontFamily: "'DM Mono',monospace" }}>changes apply within ~30s</span>
      </div>

      {FLAGS.map((f) => {
        const on = Boolean(data[f.key]);
        return (
          <div key={f.key} style={{ background: "#13201a", border: "1px solid #1c2b25", borderRadius: 12, padding: 18, marginBottom: 14, display: "flex", alignItems: "center", gap: 18 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#cfe6da", marginBottom: 4 }}>{f.label}</div>
              <div style={{ fontSize: 12, color: "#7aad98", lineHeight: 1.55 }}>{f.desc}</div>
            </div>
            <button
              onClick={() => toggle(f.key, on)}
              disabled={busy === f.key}
              style={{
                width: 56, height: 30, borderRadius: 15, border: "none", cursor: "pointer",
                background: on ? "#2d7a62" : "#1c2b25",
                position: "relative", flexShrink: 0, transition: "background 0.15s",
                opacity: busy === f.key ? 0.5 : 1,
              }}
              aria-pressed={on}
            >
              <span style={{
                position: "absolute", top: 3, left: on ? 29 : 3, width: 24, height: 24, borderRadius: "50%",
                background: "#cfe6da", transition: "left 0.15s",
              }} />
            </button>
            <span style={{ width: 42, textAlign: "left", fontFamily: "'DM Mono',monospace", fontSize: 12, color: on ? "#7ecfb0" : "#5a8a78", letterSpacing: "0.06em" }}>
              {on ? "ON" : "OFF"}
            </span>
          </div>
        );
      })}

      <div style={{ marginTop: 32, marginBottom: 12 }}>
        <div style={{ fontSize: 18, fontFamily: "'Playfair Display',serif", color: "#cfe6da", marginBottom: 4 }}>AI Model Routing</div>
        <div style={{ fontSize: 12, color: "#5a8a78" }}>Override the model tier for each feature. Default = code setting. Changes apply within ~30s.</div>
      </div>

      {TASK_GROUPS.map(({ group, tasks }) => (
        <div key={group}>
          <div className="a-tier-group-h">{group}</div>
          {tasks.map(({ key, label, codeDefault }) => {
            const current = data[key] || null;
            const effective = current || codeDefault;
            return (
              <div key={key} className="a-tier-row" style={{ opacity: busy === key ? 0.6 : 1 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, color: "#cfe6da", fontWeight: 500 }}>{label}</div>
                  <div style={{ fontSize: 11, color: "#5a8a78", fontFamily: "'DM Mono',monospace", marginTop: 2 }}>
                    {current ? `override: ${current}` : `default: ${codeDefault}`}
                  </div>
                </div>
                <div className="a-tier-btns">
                  {MODEL_TIERS.map((tier) => {
                    const isActive = effective === tier;
                    const isOverride = current === tier;
                    return (
                      <button
                        key={tier}
                        disabled={busy === key}
                        onClick={() => setTier(key, current === tier ? null : tier)}
                        className={`a-tier-btn${isActive ? ` active-${tier}` : ""}`}
                        title={`${TIER_LABELS[tier]} — ${TIER_DESC[tier]}${!isOverride && isActive ? " (code default)" : isOverride ? " (override — click to reset)" : ""}`}
                      >
                        {TIER_LABELS[tier]}
                        {isOverride && <span style={{ marginLeft: 4, opacity: 0.7 }}>×</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </>
  );
}

// ─── Login screen ────────────────────────────────────────────────────────────
function AdminLogin({ onAccess }) {
  const [token, setToken] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    const cleaned = token.trim();
    try {
      const res = await fetch(`/api/admin/summary?_t=${Date.now()}`, {
        headers: { "x-admin-token": cleaned },
        cache: "no-store",
      });
      let body = "";
      try { body = JSON.stringify(await res.json()); } catch {}
      if (res.ok) {
        sessionStorage.setItem(TOKEN_KEY, cleaned);
        onAccess();
        return;
      }
      // Show the actual server response so iPad / no-DevTools users can see what's happening.
      if (res.status === 401)      setErr("401 Unauthorized — ADMIN_SECRET on the server doesn't match what you entered. Check Vercel env vars for whitespace, quotes, or wrong scope (Production vs Preview).");
      else if (res.status === 404) setErr("404 Not Found — /api/admin/* route isn't deployed yet. Wait for Vercel build to finish.");
      else if (res.status === 500 && body.includes("ADMIN_SECRET not configured"))
                                   setErr("500 — ADMIN_SECRET env var is not set on the server (or not set for this environment scope).");
      else if (res.status === 500 && body.includes("Supabase not configured"))
                                   setErr("500 — Supabase env vars (SUPABASE_URL / SUPABASE_SERVICE_ROLE_KEY) are missing on the server.");
      else                          setErr(`${res.status} — ${body || "(empty response)"}`);
    } catch (e) {
      setErr(`Network error: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="a-login">
      <style>{CSS}</style>
      <form className="a-login-box" onSubmit={submit}>
        <h1>6zero.ai · admin</h1>
        <p>Enter the admin secret to continue.</p>
        <input
          type="password"
          autoFocus
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="ADMIN_SECRET"
        />
        <button type="submit">Enter →</button>
        {err && <div className="a-login-err">{err}</div>}
      </form>
    </div>
  );
}

// ─── Broker access (RBAC) ──────────────────────────────────────────────────────
// Broker role used to be self-service — anyone who found /broker and signed
// in got granted it automatically. Now gated: only an allowlisted email is
// granted broker on first sign-in; an existing account can also be promoted
// directly. See AuthGate.jsx syncProfile + api/admin/[...path].js brokerAccess.
function BrokerAccess() {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [invited, setInvited] = useState(null); // email of the most recently invited row

  const reload = useCallback(() => {
    apiCall("broker-access").then(setData).catch((e) => setErr(e.message));
  }, []);
  useEffect(reload, [reload]);

  const run = async (action, targetEmail) => {
    setBusy(true); setErr(null);
    try {
      await apiCall("broker-access", { action, email: targetEmail }, { method: "POST" });
      if (action === "add") setEmail("");
      if (action === "invite") { setInvited(targetEmail); setTimeout(() => setInvited(null), 4000); }
      else reload();
    } catch (e) {
      setErr(e.message);
    }
    setBusy(false);
  };

  if (!data) return <div className="a-loading">Loading…</div>;

  return (
    <>
      <div className="a-h"><h1>Broker access</h1></div>

      <div className="a-section">
        <div className="a-section-h">Allowlist a new email</div>
        <div className="a-filters">
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="broker@example.com"
            style={{ width: 260 }}
            onKeyDown={(e) => { if (e.key === "Enter" && email.trim()) run("add", email.trim()); }}
          />
          <button className="a-btn" disabled={busy || !email.trim()} onClick={() => run("add", email.trim())}>
            + Allow
          </button>
        </div>
        <p style={{ fontSize: 12, color: "#9bbfb2", marginTop: 8 }}>
          Anyone signing in at /broker with an allowlisted email is granted broker access automatically on first sign-in. No account needed yet — add the email before they ever sign in.
        </p>
        {err && <div className="a-login-err" style={{ marginTop: 8 }}>{err}</div>}
      </div>

      <div className="a-section">
        <div className="a-section-h">Allowlisted emails · {data.allowlist.length}</div>
        <table className="a-table">
          <thead><tr><th>Email</th><th>Added</th><th></th><th></th></tr></thead>
          <tbody>
            {data.allowlist.length === 0 && <tr><td colSpan={4} className="a-empty">No allowlisted emails yet</td></tr>}
            {data.allowlist.map((r) => (
              <tr key={r.email}>
                <td className="a-mono">{r.email}</td>
                <td className="a-mono">{fmtDate(r.created_at)}</td>
                <td>
                  <button className="a-btn a-btn-ghost" disabled={busy} onClick={() => run("invite", r.email)}>
                    {invited === r.email ? "Sent ✓" : "Send invite"}
                  </button>
                </td>
                <td><button className="a-btn a-btn-ghost" disabled={busy} onClick={() => run("remove", r.email)}>Remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="a-section">
        <div className="a-section-h">Current brokers (profiles.role = 'broker') · {data.brokers.length}</div>
        <table className="a-table">
          <thead><tr><th>Email</th><th>Name</th><th>Since</th><th></th></tr></thead>
          <tbody>
            {data.brokers.length === 0 && <tr><td colSpan={4} className="a-empty">No brokers yet</td></tr>}
            {data.brokers.map((b) => (
              <tr key={b.id}>
                <td className="a-mono">{b.email}</td>
                <td>{b.name || "—"}</td>
                <td className="a-mono">{fmtDate(b.created_at)}</td>
                <td><button className="a-btn a-btn-ghost" disabled={busy} onClick={() => run("demote", b.email)}>Revoke</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={{ fontSize: 12, color: "#9bbfb2", marginTop: 8 }}>
          "Revoke" clears their role immediately — the broker sandbox (App.jsx / AuthGate) means they'll be signed out of /broker on their next request and won't be able to sign back in unless re-allowlisted.
        </p>
      </div>

      <div className="a-section">
        <div className="a-section-h">Promote an existing account</div>
        <p style={{ fontSize: 12, color: "#9bbfb2", marginBottom: 8 }}>
          For someone who already has a 6zero.ai account under a different role (buyer/seller/vendor) and needs broker access granted directly, without waiting for their next sign-in.
        </p>
        <div className="a-filters">
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="existing-account@example.com" style={{ width: 260 }} />
          <button className="a-btn" disabled={busy || !email.trim()} onClick={() => run("promote", email.trim())}>
            Promote to broker
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Top-level shell ─────────────────────────────────────────────────────────
const NAV = [
  { id: "summary", label: "Summary" },
  { id: "events", label: "Events" },
  { id: "logins", label: "Logins" },
  { id: "unique", label: "Unique" },
  { id: "users", label: "Users" },
  { id: "chats", label: "Chats" },
  { id: "properties", label: "Properties" },
  { id: "vendors", label: "Vendors" },
  { id: "broker-access", label: "Broker access" },
  { id: "settings", label: "Settings" },
];

export default function AdminApp() {
  const [authed, setAuthed] = useState(() => Boolean(sessionStorage.getItem(TOKEN_KEY)));
  const [view, setView] = useState("summary");
  const [drillUserId, setDrillUserId] = useState(null);
  const [drillChatId, setDrillChatId] = useState(null);

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  if (!authed) return <AdminLogin onAccess={() => setAuthed(true)} />;

  const renderView = () => {
    if (drillUserId) return <UserDetail id={drillUserId} onBack={() => setDrillUserId(null)} onPickChat={(id) => { setDrillUserId(null); setDrillChatId(id); }} />;
    if (drillChatId) return <ChatDetail id={drillChatId} onBack={() => setDrillChatId(null)} />;
    if (view === "summary")    return <Summary />;
    if (view === "events")     return <Events />;
    if (view === "logins")     return <Logins />;
    if (view === "unique")     return <Unique />;
    if (view === "users")      return <Users onPick={setDrillUserId} />;
    if (view === "chats")      return <Chats onPick={setDrillChatId} />;
    if (view === "properties") return <Properties />;
    if (view === "vendors")    return <Vendors />;
    if (view === "broker-access") return <BrokerAccess />;
    if (view === "settings")   return <Settings />;
    return null;
  };

  return (
    <div className="a-shell">
      <style>{CSS}</style>
      <aside className="a-side">
        <div className="a-logo">
          <svg className="a-logo-svg" width="28" height="28" viewBox="0 0 40 40" fill="none">
            <rect width="40" height="40" rx="10" fill="#1c2b25"/>
            <path d="M20 8L7 18V33H16V25H24V33H33V18L20 8Z" fill="#7ecfb0"/>
            <rect x="17" y="25" width="6" height="8" fill="#1c2b25" opacity="0.4"/>
          </svg>
          <div className="a-logo-text">
            <div className="a-logo-name">6zero.ai</div>
            <div className="a-logo-sub">admin</div>
          </div>
        </div>
        <nav className="a-nav">
          {NAV.map((n) => (
            <button
              key={n.id}
              className={view === n.id && !drillUserId && !drillChatId ? "active" : ""}
              onClick={() => { setView(n.id); setDrillUserId(null); setDrillChatId(null); }}
            >
              {n.label}
            </button>
          ))}
          <div className="a-signout-wrap">
            <button onClick={logout}>Sign out</button>
          </div>
        </nav>
      </aside>
      <main className="a-main">{renderView()}</main>
    </div>
  );
}
